/** Pixel inspector: full readout of the pixel under the cursor and a numeric loupe. */
import { signal } from '@preact/signals';
import { cctFromXy } from '../../core/color';
import { quantityInfo, readPixel } from '../../core/pipeline';
import { fmt } from '../../core/units';
import { colormapCss } from '../../core/colormaps';
import { hover, measurement, quantity, windowRange } from '../../app/store';
import { NumberInput, Toggle } from '../controls';

const loupeSize = signal(7);
const frozen = signal<{ x: number; y: number } | null>(null);
export const inspectorFreeze = frozen;

export function InspectorPanel() {
  const m = measurement.value;
  const hv = hover.value;
  const pos = frozen.value ?? hv;
  if (!m) return <div class="muted pad">Move the cursor over an image to inspect pixels.</div>;
  if (!pos)
    return (
      <div class="muted pad">
        Move the cursor over the image. Press <kbd>I</kbd> to freeze the inspector position.
      </div>
    );
  const ix = Math.floor(pos.x);
  const iy = Math.floor(pos.y);
  const r = readPixel(m.ctx, quantity.value, ix, iy, m.clip);
  const p = m.plane;
  const n = loupeSize.value;
  const h = Math.floor(n / 2);
  const [lo, hi] = windowRange.value;
  const q = quantityInfo(quantity.value);
  const cct = r ? cctFromXy(r.chroma[0], r.chroma[1]) : null;
  const f = m.ctx.frame;
  const value = (x: number, y: number) => {
    const lx = x - p.ox;
    const ly = y - p.oy;
    return lx >= 0 && ly >= 0 && lx < p.width && ly < p.height ? p.data[ly * p.width + lx] : NaN;
  };
  return (
    <div class="inspector-panel" data-testid="inspector">
      <div class="toolbar-row">
        <span class="mono">
          x {ix} · y {iy}
        </span>
        {frozen.value && <span class="badge">frozen</span>}
        <span class="spacer" />
        <Toggle
          label="Freeze"
          checked={!!frozen.value}
          help="inspector.freeze"
          onChange={(v) => (frozen.value = v && hv ? { ...hv } : null)}
        />
      </div>
      {!r ? (
        <div class="muted">Outside the image</div>
      ) : (
        <table class="kv inspector-kv">
          <tbody>
            <tr data-help="inspector.value">
              <th>{q.short}</th>
              <td class="mono strong">
                {fmt(m.mode === 'A' || m.mode === 'B' ? r.value : value(ix, iy), 6)} {m.unit}
              </td>
            </tr>
            <tr data-help="inspector.codes">
              <th>Code values</th>
              <td class="mono">
                {f.channels === 1 ? fmt(r.codes[0], 6) : r.codes.map((c) => fmt(c, 6)).join(' · ')}
                {f.isFloat ? '' : ` / ${f.maxCode}`}
              </td>
            </tr>
            <tr data-help="inspector.linear">
              <th>Linear RGB</th>
              <td class="mono">{r.linear.map((c) => fmt(c, 5)).join(' · ')}</td>
            </tr>
            <tr data-help="inspector.xyz">
              <th>XYZ (rel.)</th>
              <td class="mono">{r.XYZ.map((c) => fmt(c, 5)).join(' · ')}</td>
            </tr>
            <tr data-help="inspector.chroma">
              <th>x, y</th>
              <td class="mono">
                {fmt(r.chroma[0], 4)}, {fmt(r.chroma[1], 4)}
                {cct?.valid ? ` · ${Math.round(cct.cct)} K` : ''}
              </td>
            </tr>
            {r.clip ? (
              <tr>
                <th>Flags</th>
                <td class="warn-text">
                  {r.clip & 1 ? 'saturated ' : ''}
                  {r.clip & 2 ? 'black-clipped' : ''}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}
      <div class="toolbar-row">
        <span class="muted small" data-help="inspector.loupe">
          Loupe ({q.short}, {m.unit || 'value'})
        </span>
        <span class="spacer" />
        <span class="field-inline">
          size
          <NumberInput value={n} min={3} max={15} step={2} integer onChange={(v) => (loupeSize.value = v % 2 ? v : v + 1)} />
        </span>
      </div>
      <div class="loupe" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }} data-help="inspector.loupe">
        {Array.from({ length: n * n }, (_, i) => {
          const x = ix - h + (i % n);
          const y = iy - h + Math.floor(i / n);
          const v = value(x, y);
          const t = (v - lo) / (hi - lo);
          const center = x === ix && y === iy;
          return (
            <div
              key={i}
              class={`loupe-cell${center ? ' center' : ''}`}
              style={{ background: Number.isFinite(v) ? colormapCss('gray', t) : 'transparent', color: t > 0.55 ? '#000' : '#fff' }}
              title={`(${x}, ${y})`}
            >
              {Number.isFinite(v) ? fmt(v, 3) : ''}
            </div>
          );
        })}
      </div>
    </div>
  );
}
